angular
    .module('userNotifications.v1')
    .directive('requiresActionList', requiresActionList)
    .directive('requiresActionListItemWrapper', requiresActionListItemWrapper)
    .directive('requiresActionListItem', requiresActionListItem)
    .directive('requiresActionListItemDismiss', requiresActionListItemDismiss);

/**
 * @ngdoc directive
 * @name userNotifications.v1.directive:requiresActionList
 * @restrict A
 * @element any
 *
 * @description
 * Displays a list of all notifications marked as "requires action," and exposes notification
 * data to the theme markup. Includes a dismiss directive that marks the notification as
 * resolved when clicked.
 *
 * **Requirements:** A `requires-action-list-item` element must be a child of this element. See example.
 *
 * #### Data
 * - `{{message}}` : the notification text
 * - `{{orderNumber}}` : the order number each notification is associated with
 * - `{{orderHref}}` : the href of the associated order
 *
 * #### Actions
 * - `requires-action-list-item-dismiss` : dismisses notification when clicked. While the dismissal
 * request is in progress, this element receives an `isPending` class and a `disabled` attribute.
 *
 * #### Script file
 * - `/web3/build/storefront/widgets/requires-action-list.v1.directive.js`
 *
 * #### Classes
 * - `isPending` : applied while notifications are in the proces of loading
 * - `isEmpty` : applied when there are 0 notifications in the list
 *
 * #### Events
 * - `window.events.REQUIRES_ACTION_LIST_UPDATED` : raised whenever the notification list changes
 *
 * @example
    <pre>
        <!-- load widget script -->
        <script src="/web3/build/storefront/widgets/requires-action-list.v1.directive.js"></script>

        <!-- display widget -->
        <div requires-action-list>
            <div requires-action-list-item>
                <p>Message: {{message}}</p>
                <p>Order Number: <a href="{{orderHref}}">{{orderNumber}}</a></p>
                <button requires-action-list-item-dismiss>Dismiss Notification</button>
            </div>
        </div>

        <!-- respond to events -->
        <script>
            window.events.listen(window.events.REQUIRES_ACTION_LIST_UPDATED, function () {
                console.log('requires action list updated');
            });
        </script>
    </pre>
    *
    * #### Output markup:
    * - This directive adds an unordered list between the `requires-action-list` element and the
    * `requires-action-list-item` elements.
    *
    * ```
    *       <div requires-action-list>
    *           <ul class="notificationList notificationList_requiresAction">
    *               <li class="notificationList-item">
    *                   <div requires-action-list-item>
    *                       ...
    *                   </div>
    *               </li>
    *               ...
    *           </ul
    *       </div>
    * ```
    *
    */

requiresActionList.$inject = [];

function requiresActionList () {
    return {
        restrict: 'A',
        transclude: true,
        template: '<ul class="notificationList notificationList_requiresAction"><li class="notificationList-item" ng-repeat="notification in requiresActionListController.notifications"><div requires-action-list-item-wrapper="notification" class="notificationList-item-wrapper"><ng-transclude /></div></li></ul>',
        link: function requiresActionListDirective_linkFn (scope, element, attrs, controller, transclude) {},
        controller: RequiresActionListController,
        controllerAs: 'requiresActionListController',
        bindToController: true,
        scope: {}
    };
};

RequiresActionListController.$inject = ['$scope', '$element', 'userNotificationsService', 'authService', '$timeout'];

function RequiresActionListController ($scope, $element, userNotificationsService, authService, $timeout) {
    var vm = this;

    vm.notifications = [];

    init();

    return;

    function init () {
        $element
            .addClass('isEmpty')
            .addClass('isPending');
        // Subscribe to auth changes
        authService.subscribe($scope, handleAuthChange);
        // Subscribe to requiresActionItems updates
        userNotificationsService.requiresActionItems.subscribe($scope, handleRequiresActionItemsUpdate);
        // Subscribe to classic events
        userNotificationsService.classicEvents.subscribe($scope, getRequiresActionItems);
        // Initial loading of requiresActionItems
        getRequiresActionItems();
    }

    function handleAuthChange ($event, user) {
        if (!authService.isLoggedIn()) {
            return resetDirective();
        }

        getRequiresActionItems();
    }

    function getRequiresActionItems () {
        if (!authService.isLoggedIn()) {
            return resetDirective();
        }

        userNotificationsService.requiresActionItems.get(authService.getUser().id);
    }

    function handleRequiresActionItemsUpdate ($event, notifications) {
        if (!notifications) {
            return resetDirective();
        }

        if (!notifications.length) {
            $element.addClass('isEmpty');
        } else {
            $element.removeClass('isEmpty');
        }

        $timeout(function(){
            vm.notifications = notifications;
        });
        $element
            .show()
            .removeClass('isPending');
    }

    function resetDirective () {
        vm.notifications = [];
        $element
            .hide()
            .addClass('isEmpty')
            .removeClass('isPending');
    }
};


requiresActionListItemWrapper.$inject = [];

function requiresActionListItemWrapper () {
    return {
        restrict: 'A',
        transclude: true,
        template: '<ng-transclude />',
        link: function requiresActionListItemWrapperDirective_linkFn (scope, element, attrs, controller, transclude) {},
        controller: RequiresActionListItemWrapperController,
        controllerAs: 'requiresActionListItemWrapperController',
        bindToController: true,
        scope: {
            notification: '<requiresActionListItemWrapper'
        }
    };
};

RequiresActionListItemWrapperController.$inject = [];

function RequiresActionListItemWrapperController () {};


requiresActionListItem.$inject = [];

function requiresActionListItem () {
    return {
        restrict: 'A',
        require: '^requiresActionListItemWrapper',
        transclude: true,
        template: '<a href="{{orderHref}}" rel="nofollow"><p class="order-number">Order #<span>{{orderNumber}}</span></p></a><p class="notification-message">"<span>{{message}}</span>"</p><a href="{{orderHref}}" rel="nofollow" class="review-notification">Review</a>',
        link: function requiresActionItemDirective_linkFn (scope, element, attrs, requiresActionListItemWrapperController, transclude) {
            scope.message = angular.element('<div>' + requiresActionListItemWrapperController.notification.message + '</div>').text();
            scope.orderNumber = requiresActionListItemWrapperController.notification.orderNumber;
            scope.orderHref = requiresActionListItemWrapperController.notification.orderStorefrontLink;
        }
    };
};


requiresActionListItemDismiss.$inject = ['userNotificationsService', 'authService'];

function requiresActionListItemDismiss (userNotificationsService, authService) {
    return {
        restrict: 'A',
        require: '^requiresActionListItemWrapper',
        link: function requiresActionItemDirective_linkFn (scope, element, attrs, requiresActionListItemWrapperController, transclude) {
            var pending = false;

            scope.$on('$destroy', handleDestroy);
            element.on('click', handleClick);

            function handleDestroy () {
                element.off('click', handleClick);
            }

            function handleClick (e) {
                if (pending || !authService.isLoggedIn()){
                    return;
                }

                enableLoadingState();

                userNotificationsService.requiresActionItems.resolve(authService.getUser().id, requiresActionListItemWrapperController.notification.id).finally(disableLoadingState);
            }

            function enableLoadingState () {
                pending = true;

                element
                    .prop('disabled', true)
                    .addClass('isPending');
            }

            function disableLoadingState () {
                pending = false;

                element
                    .prop('disabled', false)
                    .removeClass('isPending');
            }
        }
    };
};